import { useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useApi } from '@adiox/ui';
import { useStream } from '../stream/stream';
import { useActiveLogin } from '../trading/useTrading';
import { fromServer, toServer, type PriceAlert } from './alertModel';

const ALERT_EVENTS = new Set(['alert', 'alert_triggered', 'alert_removed']);

type Outcome = { ok: true } | { ok: false; message: string };

const failure = (status: number, body: unknown): Outcome => {
  const problem = (body ?? {}) as { detail?: string };
  if (status === 403) return { ok: false, message: 'This is a read-only session: alerts cannot be changed.' };
  if (status === 404) return { ok: false, message: 'That alert no longer exists.' };
  const detail = problem.detail
    ? problem.detail.charAt(0).toUpperCase() + problem.detail.slice(1) + '.'
    : null;
  return { ok: false, message: detail ?? 'The server did not accept that. Try again.' };
};

/**
 * The active account's price alerts, kept and watched by the server (so they go off with no terminal open). The list is read
 * again whenever the hub reports an alert change for the account.
 */
export function useAlerts() {
  const api = useApi();
  const login = useActiveLogin();
  const queryClient = useQueryClient();
  const lastChange = useStream(
    (s) =>
      s.recentEvents.findLast((e) => ALERT_EVENTS.has(e.event.type) && e.event.login === login)?.seq ?? 0,
  );
  const key = ['alerts', login, lastChange];
  const query = useQuery({
    queryKey: key,
    enabled: login !== undefined,
    placeholderData: (previous) => previous,
    queryFn: async () => {
      const { data } = await api.GET('/api/v1/accounts/{login}/alerts', {
        params: { path: { login: login! } },
      });
      return (data ?? []).map(fromServer);
    },
  });
  const alerts = useMemo(() => query.data ?? [], [query.data]);
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['alerts', login] });

  const call = async (request: () => Promise<{ response: Response; error?: unknown }>): Promise<Outcome> => {
    try {
      const { response, error } = await request();
      if (!response.ok) return failure(response.status, error);
      await refresh();
      return { ok: true };
    } catch {
      return { ok: false, message: 'Cannot reach the server. Check your connection and try again.' };
    }
  };
  const path = (id?: string) => ({ path: { login: login!, id: Number(id) } });

  return {
    login,
    alerts,
    loaded: query.data !== undefined,
    add: (draft: Pick<PriceAlert, 'symbol' | 'field' | 'direction' | 'price'>) =>
      call(() =>
        api.POST('/api/v1/accounts/{login}/alerts', {
          params: { path: { login: login! } },
          body: toServer(draft),
        }),
      ),
    setActive: (id: string, active: boolean) =>
      call(() => api.PUT('/api/v1/accounts/{login}/alerts/{id}', { params: path(id), body: { active } })),
    remove: (id: string) =>
      call(() => api.DELETE('/api/v1/accounts/{login}/alerts/{id}', { params: path(id) })),
  };
}
